import { api } from '@libs/focus-frontend-api/src';
import {
  getPreferenceFromSessionStorage,
  savePreferenceToSessionStorage,
  SessionStorageKey,
} from '@libs/focus-frontend-services/src/SessionStorageService';
import {
  Capability,
  CapabilityAccess,
  CapabilityEvaluation,
  CapabilityEvaluationRequest,
  CapabilityType,
} from '@libs/focus-frontend-types/src/@teamleader/api/Authorization/capability';
import {
  PolicyEvaluation,
  PolicyEvaluationRequest,
} from '@libs/focus-frontend-types/src/@teamleader/api/Authorization/policy';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';

export { CapabilityType };

export interface AuthorizationService {
  capabilities: Capabilities;
  getCapability: (capability: CapabilityType) => boolean;
  getCapabilities: () => Capabilities;
  fetchCapabilities: () => Promise<Capabilities>;
  saveCapabilities: (capabilities: Capabilities) => void;
  evaluateCapability: (request: CapabilityEvaluationRequest) => Promise<CapabilityEvaluation[] | undefined>;
  evaluatePolicies: (requests: PolicyEvaluationRequest[]) => Promise<PolicyEvaluation[] | undefined>;
}

export interface Capabilities extends Record<CapabilityType, boolean> {}

const defaultCapabilities = Object.values(CapabilityType).reduce((result, key) => {
  result[key] = false;
  return result;
}, {} as Capabilities);

function isServerError(error: unknown) {
  const statusCode = (error as { status?: number }).status;
  return !!statusCode && statusCode >= 500;
}

function buildCapabilityMap(capabilities: Capability[]) {
  return capabilities.reduce<Capabilities>(
    (result, capability) => ({
      ...result,
      [capability.name]: capability.access === CapabilityAccess.Granted,
    }),
    defaultCapabilities,
  );
}

async function fetchApiCapabilities(maxRetries: number = 4) {
  try {
    const response: { data: Capability[] } | null = await api.capabilities.list();
    if (isNull(response)) {
      throw new Error('Failed to fetch capabilities');
    }

    return response.data;
  } catch (error) {
    if (maxRetries > 0 && isServerError(error)) {
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      return await fetchApiCapabilities(maxRetries - 1);
    }

    throw error;
  }
}

async function fetchAndMapCapabilities() {
  const capabilities = await fetchApiCapabilities();
  return buildCapabilityMap(capabilities);
}

export function saveCapabilities(capabilities: Capabilities) {
  savePreferenceToSessionStorage<Capabilities>(SessionStorageKey.AuthorizationCapabilities, capabilities);
  window.services.authorization.capabilities = capabilities;
}

export async function fetchCapabilities() {
  return await fetchAndMapCapabilities();
}

export function getCapabilities() {
  return window.services.authorization.capabilities;
}

export function getCapability(capability: CapabilityType): boolean {
  const capabilities = getCapabilities();

  const capabilityValue = capabilities[capability];

  if (isUndefined(capabilityValue)) {
    return false;
  }

  return capabilityValue;
}

export async function evaluateCapability(request: CapabilityEvaluationRequest, maxRetries: number = 4) {
  try {
    const response: { data: CapabilityEvaluation[] } | null = await api.capabilities.evaluate(request);

    if (isNull(response)) {
      throw new Error('Failed to evaluate capability');
    }

    return response.data;
  } catch (error) {
    if (maxRetries > 0 && isServerError(error)) {
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      return await evaluateCapability(request, maxRetries - 1);
    }

    throw error;
  }
}

export async function evaluatePolicies(requests: PolicyEvaluationRequest[], maxRetries: number = 4) {
  try {
    if (!requests.length) {
      return Promise.resolve([]);
    }

    const response: { data: PolicyEvaluation[] } | null = await api.policies.evaluate(requests);
    if (isNull(response)) {
      throw new Error('Failed to evaluate policies');
    }

    return response.data;
  } catch (error) {
    if (maxRetries > 0 && isServerError(error)) {
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      return await evaluatePolicies(requests, maxRetries - 1);
    }

    throw error;
  }
}

export async function initializeCapabilities() {
  window.services = { ...(window.services ?? {}) };
  window.services.authorization = {
    capabilities: defaultCapabilities,
    getCapability,
    getCapabilities,
    fetchCapabilities,
    saveCapabilities,
    evaluateCapability,
    evaluatePolicies,
  };

  let capabilities = getPreferenceFromSessionStorage<Capabilities | undefined>(
    SessionStorageKey.AuthorizationCapabilities,
    undefined,
  );

  if (!capabilities) {
    capabilities = await fetchCapabilities();
  } else {
    fetchCapabilities()
      .then(saveCapabilities)
      .catch(() => {});
  }

  saveCapabilities(capabilities);
}
